import { request } from "../http";
import {
  ensureArrayOfStrings,
  ensureBoolean,
  ensureNumber,
  ensureObject,
  ensureString,
} from "../validate";
import type { NewsletterParams } from "./newsletter";
import { privacyConsent, type PrivacyConsentParams } from "./privacy";

const FN = "GZIntegracoes.v1.sendFormAll";

const FORM_ALL_UPSERT_URL =
  "https://tbcwr0ax1k.execute-api.us-east-1.amazonaws.com/prod/data-extension-and-pub-list-upsert/";

const DATA_EXTENSION_KEY = "BEC5F7F5-C34A-4892-BBF7-53A0C98816EC";
const DEFAULT_FONE = "999999999";
const DEFAULT_CIDADE = "XXXXXXXXXX";

export interface SendFormAllParams {
  email: string;
  name: string;
  form: string;
  fone?: string;
  cidade?: string;
  newsletter?: NewsletterParams;
  consent?: Omit<PrivacyConsentParams, "holder"> | null;
}

export async function sendFormAll(params: SendFormAllParams): Promise<boolean> {
  ensureObject(params, `${FN}(params)`);
  ensureString(params.email, `${FN} → params.email`);
  ensureString(params.name, `${FN} → params.name`);
  ensureString(params.form, `${FN} → params.form`);

  if (params.fone !== undefined && typeof params.fone !== "string") {
    throw new TypeError(
      `${FN} → params.fone deve ser uma string (recebeu: ${typeof params.fone})`,
    );
  }
  if (params.cidade !== undefined && typeof params.cidade !== "string") {
    throw new TypeError(
      `${FN} → params.cidade deve ser uma string (recebeu: ${typeof params.cidade})`,
    );
  }

  if (params.newsletter !== undefined) {
    ensureObject(params.newsletter, `${FN} → params.newsletter`);
    ensureNumber(params.newsletter.pubListId, `${FN} → params.newsletter.pubListId`);
    ensureBoolean(params.newsletter.receber, `${FN} → params.newsletter.receber`);
    ensureString(params.newsletter.code, `${FN} → params.newsletter.code`);
  }

  if (params.consent !== undefined && params.consent !== null) {
    ensureObject(params.consent, `${FN} → params.consent`);
    if (params.consent.hashes !== undefined) {
      ensureArrayOfStrings(params.consent.hashes, `${FN} → params.consent.hashes`);
    }
    if (params.consent.metadata !== undefined && params.consent.metadata !== null) {
      ensureObject(params.consent.metadata, `${FN} → params.consent.metadata`);
    }
  }

  const fone = params.fone && params.fone.length > 0 ? params.fone : DEFAULT_FONE;
  const cidade = params.cidade && params.cidade.length > 0 ? params.cidade : DEFAULT_CIDADE;
  const nl = params.newsletter;

  const body = {
    data_extension_key: DATA_EXTENSION_KEY,
    dados: [
      {
        keys: {
          email: params.email,
          form: params.form,
        },
        values: {
          usuario_id: params.email,
          nome: params.name,
          fone,
          cidade,
          newsletter: nl && nl.receber ? nl.code : "",
          newsletter_pub_list_id: nl && nl.receber ? nl.pubListId : "",
          newsletter_receber: nl?.receber ?? false,
        },
        newsletters: {
          values: nl ? [{ name: nl.code, value: nl.receber }] : [],
        },
        publicationlists: {
          values: nl ? [{ name: String(nl.pubListId), value: nl.receber }] : [],
        },
      },
    ],
  };

  const apiPromise = request(FORM_ALL_UPSERT_URL, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  const consentPromise: Promise<boolean> = params.consent
    ? privacyConsent({ ...params.consent, holder: params.email })
    : Promise.resolve(true);

  const [apiOk, consentOk] = await Promise.all([apiPromise, consentPromise]);
  return apiOk && consentOk;
}
